import { useRef, useState } from "react"
import { Space } from "antd"
import type { MenuProps } from "antd"
import { Dropdown } from "components/ui/dropdown/Dropdown"
import { ButtonOptions } from "components/ui/buttons/ButtonOptions/ButtonOptions"
import style from "./CompetitorDropdown.module.scss"

const { lazyTrigger } = style

type TItems = MenuProps["items"]

// rc-trigger installs its outside-click listener the moment it mounts, and the click
// that activated the dropdown is still propagating towards document — it arrives as a
// click-outside and would close the menu again immediately. Swallow just that one close
// rather than delaying the open: opening in the activating frame lets rc-trigger
// position the popup once, whereas opening a tick later can catch the trigger mid-scroll
// and leave the popup drifting.
const ACTIVATION_GRACE_MS = 300

type CompetitorDropdownProps = {
	/**
	 * Menu entries, or a factory returning them. Pass a factory together with
	 * `lazy` so the items are only built for the row the user actually opens.
	 */
	items: TItems | (() => TItems)
	/**
	 * Defer mounting the Ant Design Dropdown until the first click.
	 *
	 * An antd Dropdown carries a full rc-trigger instance, so one per row costs
	 * thousands of them in a large table and dominates render time. While `lazy`
	 * is set only the three-dots button is rendered (with a CSS-mask glyph rather
	 * than an inline svg); the real Dropdown is mounted, already open, on click.
	 */
	lazy?: boolean
}

export const CompetitorDropdown = ({ items, lazy = false }: CompetitorDropdownProps) => {
	const [isDropdownMounted, setDropdownMounted] = useState(!lazy)
	const [isOpen, setOpen] = useState(false)
	const activatedAtRef = useRef(0)

	if (!isDropdownMounted) {
		return (
			<div
				className={lazyTrigger}
				onClick={(e) => {
					e.stopPropagation()
					// Deliberately NOT mounted on hover: swapping this node for the real
					// Dropdown mid-gesture detaches the element a click has already
					// resolved, and the click is then lost.
					activatedAtRef.current = performance.now()
					setDropdownMounted(true)
					setOpen(true)
				}}
				data-testid="competitor-dropdown"
			>
				<ButtonOptions cssIcon style={{ backgroundColor: "transparent" }} />
			</div>
		)
	}

	return (
		<div onClick={(e) => e.stopPropagation()} data-testid="competitor-dropdown">
			{/*
			 * Space must stay: antd's Dropdown clones its child to inject the trigger's
			 * click handler and ref, and ButtonOptions forwards neither — dropping the
			 * wrapper silently stops the menu from opening.
			 */}
			<Dropdown
				items={typeof items === "function" ? items() : items}
				open={isOpen}
				onOpenChange={(next) => {
					if (!next && performance.now() - activatedAtRef.current < ACTIVATION_GRACE_MS) return
					setOpen(next)
				}}
			>
				<Space>
					<ButtonOptions style={{ backgroundColor: "transparent" }} fillColor="#DEE0EB" />
				</Space>
			</Dropdown>
		</div>
	)
}
